import { useState, useEffect, useMemo } from 'react';
import { useTranslation, useI18nStore } from '../lib/i18n';
import { api } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  ChevronLeft, 
  ChevronRight, 
  Calendar,
  CalendarRange,
  Users,
  Clock,
  Palmtree,
  Baby,
  Heart,
  Briefcase,
  CalendarDays,
  Loader2,
  Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import { 
  format, 
  startOfMonth, 
  endOfMonth, 
  startOfWeek, 
  endOfWeek, 
  eachDayOfInterval, 
  isToday, 
  addMonths, 
  subMonths, 
  addWeeks, 
  subWeeks, 
  getDay,
  isSameDay
} from 'date-fns';
import { tr, enUS, nl } from 'date-fns/locale';
import { cn } from '../lib/utils';

// Leave type colors and icons
const LEAVE_TYPE_CONFIG = {
  ANNUAL_LEAVE: { 
    color: 'bg-emerald-500', 
    lightColor: 'bg-emerald-100', 
    textColor: 'text-emerald-700',
    borderColor: 'border-emerald-300',
    icon: Palmtree 
  },
  PATERNITY_LEAVE: { 
    color: 'bg-blue-500', 
    lightColor: 'bg-blue-100', 
    textColor: 'text-blue-700',
    borderColor: 'border-blue-300',
    icon: Baby 
  },
  KLEIN_VERLET: { 
    color: 'bg-purple-500', 
    lightColor: 'bg-purple-100', 
    textColor: 'text-purple-700',
    borderColor: 'border-purple-300',
    icon: Heart 
  },
  UNPAID_LEAVE: { 
    color: 'bg-amber-500', 
    lightColor: 'bg-amber-100', 
    textColor: 'text-amber-700',
    borderColor: 'border-amber-300',
    icon: Briefcase 
  },
  DEFAULT: { 
    color: 'bg-slate-500', 
    lightColor: 'bg-slate-100', 
    textColor: 'text-slate-700',
    borderColor: 'border-slate-300',
    icon: Calendar 
  }
};

const getLeaveConfig = (code) => LEAVE_TYPE_CONFIG[code] || LEAVE_TYPE_CONFIG.DEFAULT;

const getDateLocale = (lang) => {
  switch (lang) {
    case 'tr': return tr;
    case 'nl': return nl;
    default: return enUS;
  }
};

// Generate avatar color from name
const getAvatarColor = (name) => {
  const colors = [
    'bg-rose-500', 'bg-pink-500', 'bg-fuchsia-500', 'bg-purple-500',
    'bg-violet-500', 'bg-indigo-500', 'bg-blue-500', 'bg-sky-500',
    'bg-cyan-500', 'bg-teal-500', 'bg-emerald-500', 'bg-green-500',
    'bg-lime-500', 'bg-yellow-500', 'bg-amber-500', 'bg-orange-500'
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

const getInitials = (name) => {
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

export const LeaveCalendarView = () => {
  const { t } = useTranslation();
  const { language } = useI18nStore();
  const locale = getDateLocale(language);
  
  const [viewMode, setViewMode] = useState('monthly'); // 'monthly' or 'weekly'
  const [currentDate, setCurrentDate] = useState(new Date());
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedLeaves, setSelectedLeaves] = useState([]);
  const [detailOpen, setDetailOpen] = useState(false);
  
  // Delete confirmation states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [leaveToDelete, setLeaveToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [removingDay, setRemovingDay] = useState(false);
  const [removeDayDialog, setRemoveDayDialog] = useState({ open: false, leave: null, day: null });

  // Calculate date range based on view mode
  const dateRange = useMemo(() => {
    if (viewMode === 'monthly') {
      return {
        start: startOfMonth(currentDate),
        end: endOfMonth(currentDate)
      };
    } else {
      return {
        start: startOfWeek(currentDate, { weekStartsOn: 1 }),
        end: endOfWeek(currentDate, { weekStartsOn: 1 })
      };
    }
  }, [currentDate, viewMode]);

  // Fetch calendar data
  useEffect(() => {
    const fetchCalendarData = async () => {
      setLoading(true);
      try {
        const start = format(dateRange.start, 'yyyy-MM-dd');
        const end = format(dateRange.end, 'yyyy-MM-dd');
        
        const response = await api.get(`/leave/admin/calendar?start=${start}&end=${end}`);
        if (response.data.success) {
          setLeaves(response.data.data.leaves || []);
        }
      } catch (error) {
        console.error('Error fetching calendar:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchCalendarData();
  }, [dateRange]);

  // Get leaves for a specific day
  const getLeavesForDay = (day) => {
    const dayStr = format(day, 'yyyy-MM-dd');
    return leaves.filter(leave => {
      // If selected_days is available, use exact day matching
      if (leave.selected_days && leave.selected_days.length > 0) {
        return leave.selected_days.includes(dayStr);
      }
      // Fallback to date range
      const start = new Date(leave.start_date);
      const end = new Date(leave.end_date);
      const check = new Date(dayStr);
      return check >= start && check <= end;
    });
  };

  // Check if leave is in the future (can be deleted)
  const isFutureLeave = (leave) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startDate = new Date(leave.start_date);
    return startDate >= today;
  };

  // Handle delete click
  const handleDeleteClick = (leave) => {
    setLeaveToDelete(leave);
    setDeleteDialogOpen(true);
  };

  // Confirm delete
  const handleConfirmDelete = async () => {
    if (!leaveToDelete) return;
    
    setDeleting(true);
    try {
      const response = await api.delete(`/admin/leave-requests/${leaveToDelete.id}`);
      if (response.data.success) {
        toast.success(t('leaveCalendar.deleteSuccess') || 'İzin başarıyla silindi');
        
        // Remove from local state
        setLeaves(prev => prev.filter(l => l.id !== leaveToDelete.id));
        setSelectedLeaves(prev => prev.filter(l => l.id !== leaveToDelete.id));
        
        // If balance was restored, show it
        if (response.data.data?.balance_restored) {
          toast.info(
            (t('leaveCalendar.balanceRestored') || '{days} gün bakiyeye iade edildi')
              .replace('{days}', response.data.data.days_restored)
          );
        }
        
        // Close delete dialog but keep detail dialog open
        setDeleteDialogOpen(false);
        setLeaveToDelete(null);
        
        // If no leaves left for the day, close detail dialog too
        if (selectedLeaves.length <= 1) {
          setDetailOpen(false);
        }
      }
    } catch (error) {
      console.error('Error deleting leave:', error);
      const errorMsg = error.response?.data?.detail || t('leaveCalendar.deleteError') || 'İzin silinirken hata oluştu';
      toast.error(errorMsg);
    } finally {
      setDeleting(false);
    }
  };

  // Remove only the selected day from a leave request (not the whole leave)
  const handleRemoveDayFromLeave = async (leave, dayToRemove) => {
    if (!leave || !dayToRemove || !selectedDay) return;
    
    const dayStr = format(selectedDay, 'yyyy-MM-dd');
    
    // Get selected_days from leave or generate from date range
    let selectedDays = leave.selected_days || [];
    if (selectedDays.length === 0 && leave.start_date && leave.end_date) {
      // Generate days from start to end
      const start = new Date(leave.start_date);
      const end = new Date(leave.end_date);
      const days = eachDayOfInterval({ start, end });
      selectedDays = days
        .filter(d => getDay(d) !== 0) // Exclude Sundays
        .map(d => format(d, 'yyyy-MM-dd'));
    }
    
    // If only 1 day, delete the entire leave
    if (selectedDays.length <= 1 || leave.days_count <= 1) {
      handleDeleteClick(leave);
      return;
    }
    
    // Remove the selected day
    const newSelectedDays = selectedDays.filter(d => d !== dayStr);
    
    if (newSelectedDays.length === 0) {
      handleDeleteClick(leave);
      return;
    }
    
    setRemovingDay(true);
    try {
      const sortedDays = [...newSelectedDays].sort();
      const payload = {
        selected_days: sortedDays,
        start_date: sortedDays[0],
        end_date: sortedDays[sortedDays.length - 1],
        days_count: sortedDays.length
      };
      
      const response = await api.put(`/admin/leave-requests/${leave.id}`, payload);
      if (response.data.success) {
        toast.success(
          (t('leaveCalendar.dayRemovedSuccess') || '{date} tarihli gün izinden çıkarıldı')
            .replace('{date}', format(selectedDay, 'd MMM yyyy', { locale }))
        );
        
        // Update local state
        setLeaves(prev => prev.map(l => {
          if (l.id === leave.id) {
            return {
              ...l,
              selected_days: sortedDays,
              start_date: sortedDays[0],
              end_date: sortedDays[sortedDays.length - 1],
              days_count: sortedDays.length
            };
          }
          return l;
        }));
        
        // Remove from selectedLeaves for this day
        setSelectedLeaves(prev => prev.filter(l => l.id !== leave.id));
        
        // Close dialog if no more leaves for this day
        if (selectedLeaves.length <= 1) {
          setDetailOpen(false);
        }
        
        // If balance was restored, show it
        if (response.data.data?.balance_restored) {
          toast.info(
            (t('leaveCalendar.balanceRestored') || '{days} gün bakiyeye iade edildi')
              .replace('{days}', '1')
          );
        }
      }
    } catch (error) {
      console.error('Error removing day from leave:', error);
      const errorMsg = error.response?.data?.detail || t('leaveCalendar.removeDayError') || 'Gün çıkarılırken hata oluştu';
      toast.error(errorMsg);
    } finally {
      setRemovingDay(false);
    }
  };

  // Navigation handlers
  const goToPrevious = () => {
    if (viewMode === 'monthly') {
      setCurrentDate(subMonths(currentDate, 1));
    } else {
      setCurrentDate(subWeeks(currentDate, 1));
    }
  };

  const goToNext = () => {
    if (viewMode === 'monthly') {
      setCurrentDate(addMonths(currentDate, 1));
    } else {
      setCurrentDate(addWeeks(currentDate, 1));
    }
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Calendar days for monthly view
  const monthlyCalendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
    
    // Add padding days for the start of the week
    const startDay = getDay(monthStart);
    const paddingDays = startDay === 0 ? 6 : startDay - 1; // Monday start
    
    const paddedDays = [];
    for (let i = paddingDays; i > 0; i--) {
      const d = new Date(monthStart);
      d.setDate(d.getDate() - i);
      paddedDays.push({ date: d, isCurrentMonth: false });
    }
    
    days.forEach(d => paddedDays.push({ date: d, isCurrentMonth: true }));
    
    // Add padding days for the end
    const endDay = getDay(monthEnd);
    const endPadding = endDay === 0 ? 0 : 7 - endDay;
    for (let i = 1; i <= endPadding; i++) {
      const d = new Date(monthEnd);
      d.setDate(d.getDate() + i);
      paddedDays.push({ date: d, isCurrentMonth: false });
    }
    
    return paddedDays;
  }, [currentDate]);

  // Calendar days for weekly view
  const weeklyCalendarDays = useMemo(() => {
    const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
    const weekEnd = endOfWeek(currentDate, { weekStartsOn: 1 });
    return eachDayOfInterval({ start: weekStart, end: weekEnd }).map(d => ({
      date: d,
      isCurrentMonth: true
    }));
  }, [currentDate]);

  // Stats
  const stats = useMemo(() => {
    const uniqueUsers = new Set(leaves.map(l => l.user_id)).size;
    const totalDays = leaves.reduce((sum, l) => sum + (l.days_count || 1), 0);
    return { uniqueUsers, totalDays, totalLeaves: leaves.length };
  }, [leaves]);

  const handleDayClick = (day, dayLeaves) => {
    if (dayLeaves.length === 0) return;
    setSelectedDay(day);
    setSelectedLeaves(dayLeaves);
    setDetailOpen(true);
  };

  // Localized week days
  const getWeekDays = () => {
    switch (language) {
      case 'tr': return ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];
      case 'nl': return ['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo'];
      default: return ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    }
  };

  // Get title based on view mode
  const getTitle = () => {
    if (viewMode === 'monthly') {
      return format(currentDate, 'MMMM yyyy', { locale });
    } else {
      const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
      const weekEnd = endOfWeek(currentDate, { weekStartsOn: 1 });
      return `${format(weekStart, 'd MMM', { locale })} - ${format(weekEnd, 'd MMM yyyy', { locale })}`;
    }
  };

  // Render a single day cell
  const renderDayCell = ({ date, isCurrentMonth }, isWeeklyView = false) => {
    const dayLeaves = isCurrentMonth ? getLeavesForDay(date) : [];
    const isWeekend = getDay(date) === 0 || getDay(date) === 6;
    const hasLeaves = dayLeaves.length > 0;
    const maxVisible = isWeeklyView ? 5 : 3;
    
    return (
      <div
        key={date.toISOString()}
        onClick={() => handleDayClick(date, dayLeaves)}
        className={cn(
          "border-b border-r border-slate-100 p-2 transition-colors",
          isWeeklyView ? "min-h-[200px]" : "min-h-[120px]",
          !isCurrentMonth && "bg-slate-50",
          isCurrentMonth && isWeekend && "bg-slate-50/50",
          isToday(date) && "bg-blue-50",
          hasLeaves && "cursor-pointer hover:bg-slate-50"
        )}
      >
        {/* Day number */}
        <div className={cn(
          "flex items-center justify-center w-8 h-8 rounded-full mb-2 font-medium text-sm",
          isToday(date) && "bg-slate-900 text-white",
          !isToday(date) && !isCurrentMonth && "text-slate-300",
          !isToday(date) && isCurrentMonth && isWeekend && "text-slate-400",
          !isToday(date) && isCurrentMonth && !isWeekend && "text-slate-700"
        )}>
          {format(date, 'd')}
        </div>
        
        {/* Leave indicators */}
        <div className="space-y-1">
          {dayLeaves.slice(0, maxVisible).map((leave, i) => {
            const config = getLeaveConfig(leave.leave_type_code);
            return (
              <div
                key={i}
                className={cn(
                  "flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium truncate",
                  config.lightColor,
                  config.textColor,
                  "border",
                  config.borderColor
                )}
              >
                <div className={cn("w-5 h-5 rounded-full flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0", getAvatarColor(leave.user_name))}>
                  {getInitials(leave.user_name)}
                </div>
                <span className="truncate">{leave.user_name.split(' ')[0]}</span>
              </div>
            );
          })}
          {dayLeaves.length > maxVisible && (
            <div className="text-xs text-slate-500 font-medium pl-2">
              +{dayLeaves.length - maxVisible} {t('common.more') || 'daha'}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {t('calendar.title') || 'İzin Takvimi'}
          </h2>
          <p className="text-slate-500 mt-1">
            {t('calendar.description') || 'Onaylanan izinlerin takvim görünümü'}
          </p>
        </div>
        
        {/* View Mode Tabs */}
        <div className="flex items-center gap-2">
          <Tabs value={viewMode} onValueChange={setViewMode} className="w-auto">
            <TabsList className="grid w-auto grid-cols-2">
              <TabsTrigger value="monthly" className="flex items-center gap-2 px-4">
                <CalendarDays className="h-4 w-4" />
                <span className="hidden sm:inline">{t('calendar.monthly') || 'Aylık'}</span>
              </TabsTrigger>
              <TabsTrigger value="weekly" className="flex items-center gap-2 px-4">
                <CalendarRange className="h-4 w-4" />
                <span className="hidden sm:inline">{t('calendar.weekly') || 'Haftalık'}</span>
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
        <div className="flex items-center gap-2 bg-white rounded-xl border border-slate-200 p-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={goToPrevious}
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <span className="px-4 py-2 font-semibold text-slate-800 min-w-[200px] text-center">
            {getTitle()}
          </span>
          <Button
            variant="ghost"
            size="icon"
            onClick={goToNext}
          >
            <ChevronRight className="h-5 w-5" />
          </Button>
        </div>
        
        <Button variant="outline" onClick={goToToday}>
          {t('calendar.today') || 'Bugün'}
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 bg-emerald-500 rounded-xl flex items-center justify-center">
              <Users className="h-6 w-6 text-white" />
            </div>
            <div>
              <p className="text-sm text-emerald-600 font-medium">{t('calendar.employeesOnLeave') || 'İzindeki Çalışanlar'}</p>
              <p className="text-2xl font-bold text-emerald-800">{stats.uniqueUsers}</p>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center">
              <CalendarDays className="h-6 w-6 text-white" />
            </div>
            <div>
              <p className="text-sm text-blue-600 font-medium">{t('calendar.totalLeaveDays') || 'Toplam İzin Günü'}</p>
              <p className="text-2xl font-bold text-blue-800">{stats.totalDays}</p>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center">
              <Clock className="h-6 w-6 text-white" />
            </div>
            <div>
              <p className="text-sm text-purple-600 font-medium">{t('calendar.approvedLeaves') || 'Onaylı İzin'}</p>
              <p className="text-2xl font-bold text-purple-800">{stats.totalLeaves}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 p-4 bg-slate-50 rounded-xl">
        {Object.entries(LEAVE_TYPE_CONFIG).filter(([k]) => k !== 'DEFAULT').map(([code, config]) => {
          const Icon = config.icon;
          const typeName = code === 'ANNUAL_LEAVE' ? (language === 'tr' ? 'Yıllık İzin' : 'Annual Leave') :
                          code === 'PATERNITY_LEAVE' ? (language === 'tr' ? 'Babalık İzni' : 'Paternity Leave') :
                          code === 'KLEIN_VERLET' ? (language === 'tr' ? 'Mazeret İzni' : 'Short Leave') :
                          code === 'UNPAID_LEAVE' ? (language === 'tr' ? 'Ücretsiz İzin' : 'Unpaid Leave') : code;
          return (
            <div key={code} className="flex items-center gap-2">
              <div className={cn("w-6 h-6 rounded-md flex items-center justify-center", config.color)}>
                <Icon className="h-3.5 w-3.5 text-white" />
              </div>
              <span className="text-sm text-slate-600">{typeName}</span>
            </div>
          );
        })}
      </div>

      {/* Calendar Grid */}
      <Card className="overflow-hidden">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : (
            <>
              {/* Week day headers */}
              <div className="grid grid-cols-7 bg-slate-100 border-b border-slate-200">
                {getWeekDays().map((day, i) => (
                  <div key={i} className={cn(
                    "py-3 text-center text-sm font-semibold",
                    i >= 5 ? "text-slate-400" : "text-slate-600"
                  )}>
                    {day}
                  </div>
                ))}
              </div>
              
              {/* Calendar days */}
              <div className="grid grid-cols-7">
                {(viewMode === 'monthly' ? monthlyCalendarDays : weeklyCalendarDays).map((dayData) => 
                  renderDayCell(dayData, viewMode === 'weekly')
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-slate-600" />
              {selectedDay && format(selectedDay, 'd MMMM yyyy', { locale })}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-3 max-h-[400px] overflow-y-auto">
            {selectedLeaves.map((leave, idx) => {
              const config = getLeaveConfig(leave.leave_type_code);
              const Icon = config.icon;
              const typeName = leave.leave_type_name?.[language] || leave.leave_type_name?.en || leave.leave_type_code;
              
              return (
                <div 
                  key={idx}
                  className={cn(
                    "p-4 rounded-xl border-2",
                    config.lightColor,
                    config.borderColor
                  )}
                >
                  <div className="flex items-start gap-3">
                    <div className={cn("w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold", getAvatarColor(leave.user_name))}>
                      {getInitials(leave.user_name)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-slate-900">{leave.user_name}</h4>
                      <p className="text-sm text-slate-500">{leave.user_email}</p>
                      
                      <div className="flex items-center gap-2 mt-2">
                        <Badge variant="outline" className={cn("gap-1", config.textColor, config.borderColor)}>
                          <Icon className="h-3 w-3" />
                          {typeName}
                        </Badge>
                        <Badge variant="outline" className="text-slate-600">
                          {leave.days_count} {t('leave.days') || 'gün'}
                        </Badge>
                      </div>
                      
                      <div className="mt-2 text-sm text-slate-600">
                        <span className="font-medium">{format(new Date(leave.start_date), 'd MMM', { locale })}</span>
                        {' → '}
                        <span className="font-medium">{format(new Date(leave.end_date), 'd MMM yyyy', { locale })}</span>
                      </div>
                      
                      {leave.reason && (
                        <p className="mt-2 text-sm text-slate-500 italic">
                          &ldquo;{leave.reason}&rdquo;
                        </p>
                      )}
                      
                      {/* Delete buttons - only for future leaves */}
                      {isFutureLeave(leave) && (
                        <div className="mt-3 pt-3 border-t border-slate-200 flex flex-wrap gap-2">
                          {/* Remove only this day - show only if leave has more than 1 day */}
                          {leave.days_count > 1 && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-amber-600 hover:text-amber-700 hover:bg-amber-50 border-amber-200"
                              onClick={() => setRemoveDayDialog({ open: true, leave, day: selectedDay })}
                              disabled={removingDay}
                            >
                              {removingDay ? (
                                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                              ) : (
                                <CalendarDays className="h-4 w-4 mr-1" />
                              )}
                              {t('leaveCalendar.removeThisDay') || 'Sadece Bu Günü Sil'}
                            </Button>
                          )}
                          {/* Delete entire leave */}
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                            onClick={() => handleDeleteClick(leave)}
                          >
                            <Trash2 className="h-4 w-4 mr-1" />
                            {leave.days_count > 1 
                              ? (t('leaveCalendar.deleteEntireLeave') || 'Tüm İzni Sil')
                              : (t('leaveCalendar.deleteLeave') || 'İzni Sil')
                            }
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              {t('leaveCalendar.deleteConfirmTitle') || 'İzni Sil'}
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>{t('leaveCalendar.deleteConfirmMessage') || 'Bu izni silmek istediğinizden emin misiniz?'}</p>
              {leaveToDelete && (
                <div className="mt-3 p-3 bg-slate-50 rounded-lg">
                  <p className="font-medium">{leaveToDelete.user_name}</p>
                  <p className="text-sm text-slate-600">
                    {leaveToDelete.leave_type_name?.[language] || leaveToDelete.leave_type_code}
                    {' • '}
                    {leaveToDelete.days_count} {t('leave.days') || 'gün'}
                  </p>
                  <p className="text-sm text-slate-500">
                    {format(new Date(leaveToDelete.start_date), 'd MMM', { locale })}
                    {' → '}
                    {format(new Date(leaveToDelete.end_date), 'd MMM yyyy', { locale })}
                  </p>
                </div>
              )}
              <p className="text-sm text-amber-600 mt-2">
                {t('leaveCalendar.deleteBalanceNote') || 'Onaylı izinlerde kullanılan gün bakiyeye iade edilecektir.'}
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>
              {t('common.cancel') || 'İptal'}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              {t('leaveCalendar.confirmDelete') || 'Evet, Sil'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Remove Single Day Confirmation Dialog */}
      <AlertDialog open={removeDayDialog.open} onOpenChange={(v) => !v && setRemoveDayDialog({ open: false, leave: null, day: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-600">
              <CalendarDays className="h-5 w-5" />
              {t('leaveCalendar.removeDayConfirmTitle') || 'Bu Günü Sil'}
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>{t('leaveCalendar.removeDayConfirmMessage') || 'Bu günü izinden çıkarmak istediğinizden emin misiniz?'}</p>
              {removeDayDialog.leave && removeDayDialog.day && (
                <div className="mt-3 p-3 bg-slate-50 rounded-lg">
                  <p className="font-medium">{removeDayDialog.leave.user_name}</p>
                  <p className="text-sm text-slate-600">
                    {removeDayDialog.leave.leave_type_name?.[language] || removeDayDialog.leave.leave_type_code}
                  </p>
                  <p className="text-sm font-medium text-amber-600 mt-1">
                    {format(removeDayDialog.day, 'd MMMM yyyy', { locale })}
                  </p>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removingDay}>
              {t('common.cancel') || 'İptal'}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (removeDayDialog.leave && removeDayDialog.day) {
                  handleRemoveDayFromLeave(removeDayDialog.leave, removeDayDialog.day);
                  setRemoveDayDialog({ open: false, leave: null, day: null });
                }
              }}
              disabled={removingDay}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {removingDay ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <CalendarDays className="h-4 w-4 mr-2" />
              )}
              {t('leaveCalendar.confirmRemoveDay') || 'Evet, Bu Günü Sil'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default LeaveCalendarView;
